import { useDataFormStore } from "@/stores/form";
import { getArrayOfRules } from "@/utils/getDataForm";
import { checkPay } from "@/utils/baseFunctions";
import { computed, ref, watchEffect } from "vue";

export default {
  props: {
    ruleFormRef: Object,
  },

  setup() {
    const dataForm = useDataFormStore();

    const selectedTab = ref(0);

    const payerCards = computed({
        get: () => ((dataForm).other?.payers ?? []),
        set: (val) => { (dataForm).other.payers = val; },
    });

    const optionsWhoPays = [
      {
        value: "Sender",
        label: "Отправитель",
      },
      {
        value: "Consignee",
        label: "Получатель",
      },
      {
        value: "Customer",
        label: "Третье лицо",
      },
    ];
    const optionsTypeOfPayment = [
      {
        value: "Cash",
        label: "Наличные",
      },
      {
        value: "CashlessPayments",
        label: "Расчётный счёт юр.лица",
      },
    ];

    const availablePartners = computed(() =>
      optionsWhoPays.map((p) => (p.value === "Customer" ? { ...p, disabled: dataForm.thirdPerson.cargoTransportationCustomer !== "Третье лицо" } : p)),
    );

    const convertToEnglish = (value) => value === "Отправитель" ? "Sender" : value === "Получатель" ? "Consignee" : value === "Третье лицо" ? "Customer" : ""

    const allPaymentTypeKeys = [
      "typeOfPaymentForCargoTransportation",
      "typeOfPaymentForCargoPickup",
      "typeOfPaymentForCargoDelivery",
      "typeOfPaymentForCargoExtraPacking",
    ];

    const allPayerKeys = [
      "whoPaysForCargoTransportation",
      "whoPaysForCargoPickup",
      "whoPaysForCargoDelivery",
      "whoPaysForCargoExtraPacking",
    ];

    function setAllPaymentTypes(typeOfPayment) {
      for (const key of allPaymentTypeKeys) {
        dataForm.payment[key] = typeOfPayment;
      }
    }

    // Доступные для выбора услуги
    const activeServices = computed(() => {
        const keys = ['transport'];
        if (dataForm.applicationCargoPickup.deliveryToTheAddress) keys.push('delivery');
        if (dataForm.applicationCargoPickup.isCargoPickup) keys.push('pickup');
        return keys;
    });

    watchEffect(() => {
        const senderType    = dataForm.sender?.senderType;
        const consigneeType = dataForm.consignee?.consigneeType;
        const customerType  = dataForm.thirdPerson?.typeThirdPerson;

        payerCards.value.forEach(card => {
            const person = card.person;
            card.disableCashless =
                person === "Отправитель" ? !senderType :
                person === "Получатель"  ? !consigneeType :
                person === "Третье лицо" ? !customerType :
                true;
            if (card.disableCashless && card.paymentType === "Расчётный счёт юр.лица") card.paymentType = "Наличные";
        });
    });

    const validatePayers = () => {
      let valid = true;
      let errorCardId = null;

      // Сбрасываем ошибки
      payerCards.value.forEach(card => { card.error = ''; });

      // Проверка на одинаковых плательщиков
      const personGroups = {};
      payerCards.value.forEach(card => {
        if (!personGroups[card.person]) personGroups[card.person] = [];
        personGroups[card.person].push(card);
      });
      Object.values(personGroups).forEach(group => {
        if (group.length > 1) {
          group.forEach(card => {
            card.error = 'Нельзя выбрать одинакового плательщика';
            if (errorCardId === null) errorCardId = card.id;
          });
          valid = false;
        }
      });

      // Каждая карточка плательщика должна покрывать хотя бы одну услугу
      payerCards.value.forEach(card => {
        if (!card.error) {
          const hasService = activeServices.value.some(s => !!card.services[s]);
          if (!hasService) {
            card.error = 'Выберите хотя бы одну услугу';
            if (errorCardId === null) errorCardId = card.id;
            valid = false;
          }
        }
      });

      if (errorCardId !== null) {
        selectedTab.value = errorCardId;
      }

      return valid;
    };

    return {
      dataForm,
      optionsWhoPays,
      optionsTypeOfPayment,
      payerCards,
      activeServices,
      availablePartners,
      getArrayOfRules,
      validatePayers,
      addTab: function () {
        payerCards.value.push({
            id: payerCards.value?.[payerCards.value.length - 1].id + 1,
            person: "Отправитель",
            paymentType: "Наличные",
            counter: payerCards.value.length,
            services: { transport: false, delivery: false, pickup: false },
            disableCashless: false,
            error: '',
        });
      },
      // количество выбранных условий в карточке
      cardServiceCount: function(card) {
          return activeServices.value.filter((s) => card.services[s]).length;
      },
      selectedTab,
      cargoSpaceTabsDelete: function (targetName, action) {
        if (payerCards.value.length <= 1 || action !== 'remove') return;

        // el-tabs шлёт card.id в targetName
        const idx = payerCards.value.findIndex(c => c.id === targetName);
        if (idx === -1) return;

        // Определяем на какой таб переключиться до удаления
        let activeName = selectedTab.value;
        if (activeName === targetName) {
          const nextTab = payerCards.value[idx + 1] || payerCards.value[idx - 1];
          if (nextTab) activeName = nextTab.id;
        }

        // Сохраняем услуги удаляемой карточки до splice
        const removedServices = { ...payerCards.value[idx].services };

        // Единственное удаление
        payerCards.value.splice(idx, 1);

        // Переносим услуги удалённой карточки на первую оставшуюся
        const target = payerCards.value[0];
        if (target) {
          for (const s of activeServices.value) {
            if (removedServices[s]) target.services[s] = true;
          }
        }

        // Переключаем активный таб
        selectedTab.value = activeName;
      },

      // Синхронизация типа оплаты между карточками одного плательщика
      onPaymentTypeChange: function(card, newType) {
          payerCards.value.forEach(c => {
              if (c.person === card.person) {
                  c.paymentType = newType;
              }
          });
      },

      // Смена плательщика на карточке — перенимаем тип оплаты у уже существующих карточек с таким же person
      onPersonChange: function(card, newPerson) {
          card.person = newPerson;
          const sibling = payerCards.value.find(c => c.id !== card.id && c.person === newPerson);
          if (sibling) {
              card.paymentType = sibling.paymentType;
          }
      },

      // Выбор сервиса
      onServiceToggle: function (card, service) {
          if (card.services[service]) {
              // Пользователь ВКЛЮЧИЛ — забираем услугу у остальных карточек.
              for (const donor of payerCards.value.filter(c => c.id !== card.id && c.services[service])) {
                  donor.services[service] = false;
              }
          } else {
              // Снять нельзя — всегда откатываем назад.
              card.services[service] = true;
          }
      },
      checkingPresenceThirdPerson: function (label) {
        return dataForm.thirdPerson.cargoTransportationCustomer !== 'Третье лицо' && label === 'Третье лицо'
      },

      handlerChangeWhoPays: function () {
        const value = dataForm.payment.whoPaysForCargoTransportation;
        const counterpartie = convertToEnglish(value).toLowerCase();

        // Синхронизируем все 4 поля плательщика
        for (const key of allPayerKeys) {
          dataForm.payment[key] = value;
        }

        dataForm.consigneeInnRadio = !checkPay();

        // Выставляем тип оплаты в зависимости от типа плательщика
        let typeOfPayment = 'Наличные';
        switch (counterpartie) {
          case 'sender':
            typeOfPayment = dataForm.sender.senderType ? 'Расчётный счёт юр.лица' : 'Наличные';
            break;
          case 'consignee':
            typeOfPayment = dataForm.consignee.consigneeType ? 'Расчётный счёт юр.лица' : 'Наличные';
            break;
          case 'customer':
            typeOfPayment = dataForm.thirdPerson.typeThirdPerson ? 'Расчётный счёт юр.лица' : 'Наличные';
            break;
        }

        setAllPaymentTypes(typeOfPayment);
      },

      checkPayLock: function (whoPay) {
        switch (convertToEnglish(whoPay)) {
          case 'Sender':
            return !dataForm.sender.senderType;
          case 'Consignee':
            return !dataForm.consignee.consigneeType;
          case 'Customer':
            return !dataForm.thirdPerson.typeThirdPerson;
        }
        return false;
      },

      handlerChangeTypeOfPayment: function () {
        const value = dataForm.payment.typeOfPaymentForCargoTransportation;
        setAllPaymentTypes(value);
      },
    };
  },
};
